import { DealerLayout } from "@/components/shared/DealerLayout";
import { PDFFormFiller } from "@/components/shared/PDFFormFiller";
import { uploadPreApprovedDocument } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${API}/api/templates/retailer_agreement`;

// Precise pymupdf positions
// PDF page size: 612 x 792. Coords: [x1, y1, x2, y2] bottom-left origin
const today = new Date().toISOString().split("T")[0];

const FIELDS = [
  // === PAGE 1 (0-indexed 0) - Agreement Opening ===
  // Issue 15: Moved up more, removed business_address
  // Line: "effective as of [    ]," at pymupdf y=83-94 → PDF y=698-709
  { id: "effective_date", page: 0, type: "text", rect: [383, 700, 515, 711], label: "Effective Date" },
  // "[ ] ("Retailer")" at pymupdf y=118-129 → PDF y=663-674
  { id: "retailer_name", page: 0, type: "text", rect: [74, 665, 180, 676], label: "Retailer Name" },
  // "Michigan [    ]" at pymupdf y=118-129 → PDF y=663-674
  { id: "entity_type", page: 0, type: "text", rect: [295, 665, 350, 676], label: "Entity Type" },

  // === PAGE 15 (0-indexed 14) - Notices Section ===
  // Issue 25: Retailer section text boxes
  // "Corp: ​" at [235,612,266,623]
  { id: "retailer_corp", page: 14, type: "text", rect: [270, 169, 555, 180], label: "Corp" },
  // "DBA: ​" at [235,629,264,641]
  { id: "retailer_dba", page: 14, type: "text", rect: [268, 151, 555, 163], label: "DBA" },
  // "Address: ​" at [235,647,278,658]
  { id: "retailer_address_p15", page: 14, type: "text", rect: [282, 134, 555, 145], label: "Address" },
  // "City/State/Zip: ​" at [235,664,304,675]
  { id: "retailer_csz", page: 14, type: "text", rect: [308, 117, 555, 128], label: "City/State/Zip" },
  // "Phone: ​" at [235,681,270,692]
  { id: "retailer_phone_p15", page: 14, type: "text", rect: [274, 100, 555, 111], label: "Phone" },
  // "Fax: ​" at [235,698,259,710]
  { id: "retailer_fax", page: 14, type: "text", rect: [263, 82, 555, 94], label: "Fax" },
  // "Tax ID: ​" at [235,716,272,727]
  { id: "retailer_taxid", page: 14, type: "text", rect: [276, 65, 555, 76], label: "Tax ID" },
  // "Contact: ​" at [235,733,277,744]
  { id: "retailer_contact", page: 14, type: "text", rect: [281, 48, 555, 59], label: "Contact" },

  // === PAGE 18 (0-indexed 17) - Signature Page ===
  // Issue 26: Master Agent date auto-fill
  // "Date:​" at [78,215,102,226]
  { id: "ma_date_p18", page: 17, type: "text", rect: [105, 566, 270, 577], label: "Date", defaultValue: today },

  // Issue 27: Retailer side - aligned with label positions
  // "By:​" at [299,147,321,158] → PDF y=634-645
  { id: "retailer_sig_p18", page: 17, type: "signature", rect: [325, 634, 540, 648], label: "Retailer Signature" },
  // "Name:​" at [299,169,333,181] → PDF y=611-623
  { id: "retailer_name_p18", page: 17, type: "text", rect: [335, 611, 540, 623], label: "Name" },
  // "Title:​" at [299,192,328,203] → PDF y=589-600
  { id: "retailer_title_p18", page: 17, type: "text", rect: [330, 589, 540, 600], label: "Title" },
  // "Date:​" at [299,215,328,226] → PDF y=566-577
  { id: "retailer_date_p18", page: 17, type: "text", rect: [330, 566, 540, 577], label: "Date" },

  // === PAGE 19 (0-indexed 18) - Confidentiality Agreement ===
  // Issue 33: Retailer name in section 1 - aligned with text baseline
  // "The undersigned, ​" at [138,141,212,152] → PDF y=640-651
  { id: "retailer_name_p19", page: 18, type: "text", rect: [215, 640, 315, 651], label: "Retailer Name" },

  // Issue 28: Master Agent date auto-fill
  // "Date:​" at [78,623,102,634]
  { id: "ma_date_p19", page: 18, type: "text", rect: [105, 158, 270, 169], label: "Date", defaultValue: today },

  // Issue 29: Retailer side - aligned with label positions
  // "By:​" at [299,555,321,566] → PDF y=226-237
  { id: "retailer_sig_p19", page: 18, type: "signature", rect: [325, 226, 540, 240], label: "Retailer Signature" },
  // "Name:​" at [299,578,333,589] → PDF y=203-214
  { id: "retailer_name2_p19", page: 18, type: "text", rect: [335, 203, 540, 214], label: "Name" },
  // "Title:​" at [299,600,328,611] → PDF y=181-192
  { id: "retailer_title_p19", page: 18, type: "text", rect: [330, 181, 540, 192], label: "Title" },
  // "Date:​" at [299,623,328,634] → PDF y=158-169
  { id: "retailer_date_p19", page: 18, type: "text", rect: [330, 158, 540, 169], label: "Date" },

  // === PAGE 20 (0-indexed 19) - Personal Guaranty ===
  // Issue 32: Retailer name and agreement date in guaranty text - aligned with text baseline
  // [144,118,324,129] "For value received, and in consideration for ​" → PDF y=663-674
  { id: "guaranty_retailer", page: 19, type: "text", rect: [330, 663, 520, 674], label: "Retailer Name" },
  // Agreement date at [72,141,74,152] area → PDF y=640-651
  { id: "guaranty_date_ref", page: 19, type: "text", rect: [78, 640, 158, 651], label: "Agreement Date" },

  // Issue 30: Master Agent (WITNESS) date auto-fill
  // "Date:​" at [316,637,340,649] → PDF y=143-155
  { id: "witness_date_p20", page: 19, type: "text", rect: [345, 143, 540, 155], label: "Date", defaultValue: today },

  // Issue 31: Guarantor side (left column) - aligned with labels
  // "By:​" at [77,568,94,580] → PDF y=212-224
  { id: "guarantor_sig", page: 19, type: "signature", rect: [98, 212, 258, 227], label: "Guarantor Signature" },
  // "Name:​" at [77,591,106,603] → PDF y=189-201
  { id: "guarantor_name", page: 19, type: "text", rect: [110, 189, 258, 201], label: "Name" },
  // "Title:​" at [77,614,101,626] → PDF y=166-178
  { id: "guarantor_title", page: 19, type: "text", rect: [105, 166, 258, 178], label: "Title" },
  // "Date:​" at [77,637,101,649] → PDF y=143-155
  { id: "guarantor_date", page: 19, type: "text", rect: [105, 143, 258, 155], label: "Date" },
];

const MANUAL_TEXT_MAPPING = FIELDS
  .filter((f) => f.type === "text")
  .map((f) => ({
    fieldId: f.id,
    page: f.page,
    x: f.rect[0] + 2,
    y: f.rect[1] + 3,
    fontSize: 9,
  }));

export default function RetailerAgreementGenerator() {
  const navigate = useNavigate();

  return (
    <DealerLayout>
      <PDFFormFiller
        pdfUrl={PDF_URL}
        fields={FIELDS}
        title="Exclusive Retailer Agreement"
        subtitle="Prime Media and Marketing, LLC - Fill in the highlighted fields on key pages. Blue dots indicate pages with fields."
        docType="retailer_agreement"
        onSave={(file, docType) => uploadPreApprovedDocument(file, docType).then(() => navigate("/dealer/documents"))}
        onBack={() => navigate("/dealer/documents")}
        manualTextMapping={MANUAL_TEXT_MAPPING}
      />
    </DealerLayout>
  );
}
